import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarClock,
  ScanLine,
  HardHat,
  Wrench,
  Truck,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react';
import {
  VISIBLE_STATIONS,
  stationLabel,
  stationRoleFor,
  type VisibleStation,
} from '@hcc/types';
import { useCurrentUser } from '../../hooks/useAuth';
import { COORD, hasAny } from '../../components/layout/navConfig';

const STATION_ICONS: Record<VisibleStation, LucideIcon> = {
  IT: ScanLine,
  PPE: HardHat,
  Tools: Wrench,
  Fleet: Truck,
};

/**
 * Pre-day issuance landing — pick a station, then drop into the
 * regular StationView with ?preday=true which lifts the arrived
 * filter and shows a pre-day banner. The KitIssued event itself is
 * identical to an on-the-day issuance; signatures, PDF receipts,
 * and the inductee's /me view all behave the same.
 *
 * The receipt PDF is always emailed for pre-day issuances (the
 * worker bypasses the per-station email toggle when the person
 * hasn't arrived yet).
 *
 * Shown to coordinators (all stations) and to station-role users
 * (only their own station) via the nav. Coordinators pick any station;
 * a station user sees just the station(s) they hold a role for.
 */
export function PreDayIssue() {
  const { data: user, isLoading } = useCurrentUser();
  // Coordinators pick any station; a station user sees only the ones
  // they hold a role for. A staff user who lands here with neither (via a
  // direct URL, not the nav) still sees the full picker.
  const stations = useMemo(() => {
    if (!user) return [] as readonly VisibleStation[];
    if (hasAny(user, COORD)) return VISIBLE_STATIONS;
    const own = VISIBLE_STATIONS.filter((s) => user.roles.includes(stationRoleFor(s)));
    return own.length > 0 ? own : VISIBLE_STATIONS;
  }, [user]);

  if (isLoading) {
    return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-warning-soft/15 text-warning-soft-fg">
            <CalendarClock className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-warning-soft-fg">
              Pre-day
            </div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Issue kit early
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              Hand out kit and capture signatures before mobilisation day.
              Items issued here flow through to the day's record and the
              attendee receives their signed PDF receipt by email.
            </p>
          </div>
        </header>

        <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-fg-muted">
          Pick a station
        </p>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {stations.map((s) => {
            const Icon = STATION_ICONS[s];
            return (
              <Link
                key={s}
                to={`/stations/${s}?preday=true`}
                className="group flex items-center gap-4 rounded-xl border border-line/10 bg-surface p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-warning/25 hover:shadow-md"
              >
                <div className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-warning-soft/15 text-warning-soft-fg">
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-semibold tracking-tight text-fg group-hover:text-fg-muted">
                    {stationLabel(s)}
                  </div>
                  <div className="mt-0.5 text-xs text-fg-muted">
                    Issue {stationLabel(s)} kit before the day
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-fg-dim" />
              </Link>
            );
          })}
        </div>
      </div>
    </main>
  );
}
